/**
 * Centralized Auth0 URL utilities to prevent "State parameter is invalid" errors
 * by ensuring consistent domain handling across all authentication flows
 *
 * Domain Setup: app.petboost.com (no www variant used)
 */

/**
 * Gets the current domain origin for consistent URL construction
 * This addresses the Auth0 state parameter cookie domain consistency problem
 *
 * For app.petboost.com - no normalization needed as www is never used
 */
export const normalizeCurrentDomain = (): string => {
  if (typeof window === 'undefined') return '';

  const { protocol, hostname, port } = window.location;

  // For app.petboost.com setup - no www normalization needed
  // But keeping the replace for defensive programming in case of unexpected domains
  const normalizedHostname = hostname.replace(/^www\./, '');

  // Reconstruct the origin with normalized domain
  const portPart = port ? `:${port}` : '';
  return `${protocol}//${normalizedHostname}${portPart}`;
};

/**
 * Constructs a consistent returnTo URL that works with Auth0 state parameter validation
 * Always includes the normalized domain to prevent cookie domain mismatches
 *
 * For app.petboost.com - ensures all returnTo URLs use the same domain format
 */
export const createReturnToUrl = (
  path?: string,
  searchParams?: string,
  preserveCurrentUrl = false
): string => {
  const normalizedDomain = normalizeCurrentDomain();

  if (preserveCurrentUrl) {
    // Use current URL but with normalized domain
    return typeof window !== 'undefined'
      ? `${normalizedDomain}${window.location.pathname}${window.location.search}${window.location.hash}`
      : normalizedDomain;
  }

  // Construct specific path with normalized domain
  const targetPath = path || '/';
  const queryString = searchParams ? `?${searchParams}` : '';

  return `${normalizedDomain}${targetPath}${queryString}`;
};

/**
 * Creates a login URL with consistent returnTo parameter
 */
export const createLoginUrl = (
  options: {
    returnTo?: string;
    loginHint?: string;
    phone?: string;
    preserveCurrentUrl?: boolean;
  } = {}
): string => {
  const { returnTo, loginHint, phone, preserveCurrentUrl = false } = options;

  // Use provided returnTo or create one from current location
  const finalReturnTo =
    returnTo || createReturnToUrl(undefined, undefined, preserveCurrentUrl);

  const params = new URLSearchParams();
  params.set('returnTo', finalReturnTo);

  if (loginHint) {
    params.set('login_hint', loginHint);
  }

  if (phone) {
    params.set('phone', phone);
  }

  return `/auth/login?${params.toString()}`;
};

/**
 * Creates a logout URL with consistent returnTo parameter
 */
export const createLogoutUrl = (returnTo?: string): string => {
  const finalReturnTo = returnTo || normalizeCurrentDomain();
  return `/auth/logout?returnTo=${encodeURIComponent(finalReturnTo)}`;
};

/**
 * Safe redirect function that ensures proper domain handling
 */
export const safeRedirect = (url: string): void => {
  if (typeof window === 'undefined') return;

  // Use location.assign for better browser compatibility
  window.location.assign(url);
};

/**
 * Redirect to login with consistent parameters
 */
export const redirectToLogin = (
  options: {
    returnTo?: string;
    loginHint?: string;
    phone?: string;
    preserveCurrentUrl?: boolean;
  } = {}
): void => {
  const loginUrl = createLoginUrl(options);
  safeRedirect(loginUrl);
};

/**
 * Redirect to logout with consistent parameters
 */
export const redirectToLogout = (returnTo?: string): void => {
  const logoutUrl = createLogoutUrl(returnTo);
  safeRedirect(logoutUrl);
};
